import { useState, useRef, useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { STANDARD_TIMING_FUNCTION } from '@shared/utils/animationTransition';

type UseSwipeActionConfig = Readonly<{
  threshold: number;
  duration: number;
  disabled: boolean;
  borderRadius?: string | number;
  onOpen?: () => void;
  onClose?: () => void;
}>;

const VELOCITY_THRESHOLD = 0.5;

const getReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useSwipeAction = (config: UseSwipeActionConfig) => {
  const { threshold, duration, disabled, borderRadius, onOpen, onClose } = config;

  // Only React state — triggers one re-render on swipe completion
  const [isOpen, setIsOpen] = useState(false);

  // All mutable drag state lives in refs — zero re-renders during gesture
  const contentRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const actionsWidthRef = useRef(0);
  const isDraggingRef = useRef(false);
  const rafIdRef = useRef(0);
  const isOpenRef = useRef(false);
  const activeTransitionCleanupRef = useRef<(() => void) | null>(null);

  // Safely attach a transitionend listener with setTimeout fallback and cleanup tracking.
  // Filters by target + propertyName to avoid settling early on bubbled child transitions.
  const onTransitionEnd = useEventCallback((el: HTMLElement, callback: () => void) => {
    activeTransitionCleanupRef.current?.();
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      el.removeEventListener('transitionend', handleTransitionEnd);
      clearTimeout(timerId);
      activeTransitionCleanupRef.current = null;
      callback();
    };
    const handleTransitionEnd = (e: TransitionEvent) => {
      if (e.target !== el || e.propertyName !== 'transform') return;
      settle();
    };
    el.addEventListener('transitionend', handleTransitionEnd);
    // Fallback: if transitionend never fires (element already at target, DOM removal, etc.)
    const timerId = setTimeout(settle, duration + 50);
    activeTransitionCleanupRef.current = () => {
      el.removeEventListener('transitionend', handleTransitionEnd);
      clearTimeout(timerId);
    };
  });

  const applyTransform = useEventCallback((translateX: number, animate: boolean) => {
    const el = contentRef.current;
    if (!el) return;

    const effectiveDuration = getReducedMotion() ? 0 : duration;

    if (animate) {
      el.style.transition = `transform ${effectiveDuration}ms ${STANDARD_TIMING_FUNCTION}`;
    } else {
      el.style.transition = 'none';
    }

    el.style.transform = translateX === 0 ? '' : `translateX(${translateX}px)`;
  });

  const close = useEventCallback(() => {
    if (!isOpenRef.current && !isDraggingRef.current) return;

    isDraggingRef.current = false;
    applyTransform(0, true);

    const el = contentRef.current;
    const effectiveDuration = getReducedMotion() ? 0 : duration;

    const syncClose = () => {
      el?.style.removeProperty('will-change');
      el?.style.removeProperty('transition');
      el?.style.removeProperty('transform');
      // Restore tile border radius
      const tile = el?.firstElementChild;
      if (tile instanceof HTMLElement) {
        tile.style.removeProperty('border-radius');
      }
      // Hide actions layer
      const actionsWrapper = actionsRef.current?.parentElement;
      if (actionsWrapper) {
        actionsWrapper.style.visibility = 'hidden';
      }
      const wasOpen = isOpenRef.current;
      isDraggingRef.current = false;
      isOpenRef.current = false;
      setIsOpen(false);
      if (wasOpen) {
        onClose?.();
      }
    };

    if (effectiveDuration === 0 || !el) {
      syncClose();
    } else {
      onTransitionEnd(el, syncClose);
    }
  });

  const open = useEventCallback(() => {
    // Measure actions width if not already measured (keyboard/imperative callers skip onSwipeStart)
    if (actionsRef.current) {
      actionsWidthRef.current = actionsRef.current.getBoundingClientRect().width;
      // Show actions layer (keyboard/imperative callers skip onSwipeStart)
      const actionsWrapper = actionsRef.current.parentElement;
      if (actionsWrapper) {
        actionsWrapper.style.visibility = 'visible';
      }
    }
    const width = actionsWidthRef.current;
    if (width === 0) return;

    applyTransform(-width, true);

    const el = contentRef.current;
    // Strip tile border radius for clean edges
    if (borderRadius !== undefined && el) {
      const tile = el.firstElementChild;
      if (tile instanceof HTMLElement) {
        tile.style.borderRadius = '0';
      }
    }
    const effectiveDuration = getReducedMotion() ? 0 : duration;

    const syncOpen = () => {
      el?.style.removeProperty('will-change');
      isDraggingRef.current = false;
      isOpenRef.current = true;
      setIsOpen(true);
      onOpen?.();
    };

    if (effectiveDuration === 0 || !el) {
      syncOpen();
    } else {
      onTransitionEnd(el, syncOpen);
    }
  });

  const handlers = useSwipeable({
    onSwipeStart: () => {
      // Measure actions width once at the start of each swipe
      if (actionsRef.current) {
        actionsWidthRef.current = actionsRef.current.getBoundingClientRect().width;
        // Show the actions layer (hidden by default to prevent color bleed at rounded corners)
        const actionsWrapper = actionsRef.current.parentElement;
        if (actionsWrapper) {
          actionsWrapper.style.visibility = 'visible';
        }
      }
      isDraggingRef.current = true;

      const el = contentRef.current;
      if (el) {
        el.style.willChange = 'transform';
        // Strip border radius from content and its first child (the tile) for clean sliding edges
        if (borderRadius !== undefined) {
          const tile = el.firstElementChild;
          if (tile instanceof HTMLElement) {
            tile.style.borderRadius = '0';
          }
        }
      }
    },

    onSwiping: data => {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        const width = actionsWidthRef.current;
        if (width === 0) return;

        // When already open, offset from the open position
        const baseOffset = isOpenRef.current ? -width : 0;
        const rawOffset = baseOffset + data.deltaX;

        // Clamp between -actionsWidth and 0
        const clampedOffset = Math.max(-width, Math.min(0, rawOffset));
        applyTransform(clampedOffset, false);
      });
    },

    onSwiped: data => {
      cancelAnimationFrame(rafIdRef.current);
      // isDraggingRef is cleared inside close() — keep it true here so close() doesn't bail

      const width = actionsWidthRef.current;
      if (width === 0) {
        applyTransform(0, true);
        return;
      }

      const absDistance = Math.abs(data.deltaX);
      const isFastFlick = data.velocity > VELOCITY_THRESHOLD;
      const isLeftSwipe = data.dir === 'Left';
      const isRightSwipe = data.dir === 'Right';

      if (isOpenRef.current) {
        // Currently open — close on right swipe or fast right flick
        if (isRightSwipe && (absDistance >= threshold || isFastFlick)) {
          close();
        } else {
          // Snap back to open position
          applyTransform(-width, true);
          const el = contentRef.current;
          if (el) {
            onTransitionEnd(el, () => {
              el.style.removeProperty('will-change');
            });
          }
        }
      } else {
        // Currently closed — open on left swipe past threshold or fast flick
        if (isLeftSwipe && (absDistance >= threshold || isFastFlick)) {
          open();
        } else {
          close();
        }
      }
    },

    trackTouch: true,
    trackMouse: false,
    preventScrollOnSwipe: false,
  });

  // When disabled changes to true, close if open or mid-drag (cleans up inline styles)
  useEffect(() => {
    if (disabled && (isOpenRef.current || isDraggingRef.current)) {
      close();
    }
  }, [disabled, close]);

  // Cleanup rAF and transitionend listeners on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafIdRef.current);
      activeTransitionCleanupRef.current?.();
    };
  }, []);

  return {
    handlers: disabled ? undefined : handlers,
    contentRef,
    actionsRef,
    isOpen,
    open,
    close,
  };
};
